import React, { useEffect, useMemo, useRef } from 'react';
import { createChart, HistogramSeries, LineSeries } from 'lightweight-charts';
import {
  applyVisibleRange,
  copyVisibleTimeRange,
  filterSignalBarsForPriceBars,
  syncChartsVisibleTimeRange,
} from '../utils/chartTimeRange';
import {
  buildAdtvLineData,
  buildObvEmaLineData,
  buildObvLineData,
  buildVolumeHistogramData,
  obvBadgeLabel,
  trendBadgeLabel,
} from '../utils/volumeChartData';
import { observeChartSize } from '../utils/chartResize';
import './VolumeSubCharts.css';

function VolumeSubCharts({
  visible = true,
  loading = false,
  signalBars = [],
  latestTrend = null,
  latestObv = null,
  parentChart = null,
  priceBars = null,
  zoomRange = null,
  timeMode = 'day',
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const volSeriesRef = useRef(null);
  const adtvSeriesRef = useRef(null);
  const obvSeriesRef = useRef(null);
  const obvEmaSeriesRef = useRef(null);

  const alignedSignalBars = useMemo(
    () => filterSignalBarsForPriceBars(signalBars, priceBars),
    [signalBars, priceBars],
  );

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6', scaleMargins: { top: 0.1, bottom: 0.55 } },
      leftPriceScale: { visible: false },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    const volSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: false,
    });

    const adtvSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: false,
    });

    const obvSeries = chart.addSeries(LineSeries, {
      color: '#4A17A1',
      lineWidth: 2,
      priceScaleId: 'obv',
      lastValueVisible: false,
      priceLineVisible: false,
      title: 'OBV Δ',
    });

    const obvEmaSeries = chart.addSeries(LineSeries, {
      color: '#3A17A1',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'obv',
      lastValueVisible: false,
      priceLineVisible: false,
      title: 'OBV EMA20',
    });

    chart.priceScale('obv').applyOptions({
      scaleMargins: { top: 0.55, bottom: 0.05 },
    });

    chartRef.current = chart;
    volSeriesRef.current = volSeries;
    adtvSeriesRef.current = adtvSeries;
    obvSeriesRef.current = obvSeries;
    obvEmaSeriesRef.current = obvEmaSeries;

    const disconnectResize = observeChartSize(containerRef.current, (width, height) => {
      chart.applyOptions({ width, height });
    });

    return () => {
      disconnectResize();
      chart.remove();
      chartRef.current = null;
      volSeriesRef.current = null;
      adtvSeriesRef.current = null;
      obvSeriesRef.current = null;
      obvEmaSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!volSeriesRef.current || !obvSeriesRef.current) return;

    volSeriesRef.current.setData(buildVolumeHistogramData(alignedSignalBars, timeMode));
    adtvSeriesRef.current.setData(buildAdtvLineData(alignedSignalBars, timeMode));
    obvSeriesRef.current.setData(buildObvLineData(alignedSignalBars, timeMode));
    obvEmaSeriesRef.current?.setData(buildObvEmaLineData(alignedSignalBars, timeMode));

    const chart = chartRef.current;
    if (!chart || alignedSignalBars.length === 0) return;

    if (priceBars?.length && zoomRange) {
      applyVisibleRange(chart, priceBars, zoomRange);
    } else if (parentChart) {
      copyVisibleTimeRange(parentChart, chart);
    } else {
      chart.timeScale().fitContent();
    }
  }, [alignedSignalBars, timeMode, priceBars, zoomRange, parentChart]);

  useEffect(() => {
    if (!visible || !parentChart || !chartRef.current) return undefined;
    return syncChartsVisibleTimeRange(parentChart, chartRef.current);
  }, [visible, parentChart, alignedSignalBars.length]);

  useEffect(() => {
    if (!visible || !chartRef.current || !priceBars?.length || !zoomRange) return;
    applyVisibleRange(chartRef.current, priceBars, zoomRange);
  }, [visible, priceBars, zoomRange]);

  const volumeBadge = trendBadgeLabel(latestTrend);
  const obvBadge = obvBadgeLabel(latestObv);
  const obvLabelKey = latestObv?.obv_label || 'neutral';

  return (
    <div className={`volume-sub-panel${visible ? ' visible' : ''}`}>
      <div className="volume-sub-label">
        <span>Volume · OBV (window Δ)</span>
        {obvBadge && (
          <span className={`obv-trend-badge label-${obvLabelKey}`}>{obvBadge}</span>
        )}
        {volumeBadge && (
          <span
            className={`volume-trend-badge${
              latestTrend?.confirmed ? ' confirmed' : ''
            }${latestTrend?.trend_direction ? ` dir-${latestTrend.trend_direction}` : ''}`}
          >
            {volumeBadge}
          </span>
        )}
        {loading && <span className="volume-sub-spinner">…</span>}
      </div>
      <div ref={containerRef} className="volume-sub-container" />
    </div>
  );
}

export default VolumeSubCharts;
